import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';

const TransactionHistory = ({
  walletAddress,
  web3payContract,
  contractTransactions,
  refreshTransactions
}) => {
  const [limit, setLimit] = useState(10);
  const [localContractTransactions, setLocalContractTransactions] = useState([]);
  const [contractLoading, setContractLoading] = useState(false);
  const [error, setError] = useState(null);

  // Load contract transactions
  const loadContractTransactions = async () => {
    if (!web3payContract || !walletAddress) return;

    try {
      setContractLoading(true);
      setError(null);

      // Try getFormattedTransactions first (more efficient)
      let formattedTxs = [];

      if (typeof web3payContract.getFormattedTransactions === 'function') {
        try {
          const [dates, descriptions, amounts, symbols] = await web3payContract.getFormattedTransactions(walletAddress, limit);

          formattedTxs = dates.map((date, index) => ({
            id: index,
            account: walletAddress,
            txType: getTransactionTypeFromDescription(descriptions[index]),
            description: descriptions[index],
            token: getTokenAddressFromSymbol(symbols[index]),
            tokenSymbol: symbols[index],
            amount: parseFloat(ethers.formatUnits(amounts[index], 18)),
            fee: 0, // You might need to adjust this based on your contract
            timestamp: parseInt(date),
            txHash: null, // Not available from formatted function
            createdAt: new Date(parseInt(date) * 1000).toISOString(),
            status: 'completed',
            toAddress: walletAddress,
            fromAddress: walletAddress,
            metadata: {
              paymentType: getTransactionTypeString(getTransactionTypeFromDescription(descriptions[index])),
              isContractTx: true
            }
          }));
        } catch (formatError) {
          console.warn('getFormattedTransactions failed, falling back to getRecentTransactions:', formatError);
          // Fall back to getRecentTransactions
          await loadWithRecentTransactions();
          return;
        }
      } else if (typeof web3payContract.getRecentTransactions === 'function') {
        await loadWithRecentTransactions();
        return;
      } else {
        throw new Error('No compatible transaction method found in contract');
      }

      setLocalContractTransactions(formattedTxs);
    } catch (error) {
      console.error('Failed to load contract transactions:', error);
      setError(error.message);
      setLocalContractTransactions([]);
    } finally {
      setContractLoading(false);
    }
  };

  // Fallback method using getRecentTransactions
  const loadWithRecentTransactions = async () => {
    const transactions = await web3payContract.getRecentTransactions(walletAddress, limit);

    const formattedTxs = transactions.map((tx, index) => ({
      id: Number(tx.id) || index,
      account: tx.account || walletAddress,
      txType: Number(tx.txType) || 2, // Default to transfer
      description: tx.description || 'Transaction',
      token: tx.token || ethers.ZeroAddress,
      tokenSymbol: getTokenSymbol(tx.token),
      amount: parseFloat(ethers.formatUnits(tx.amount || 0, 18)),
      fee: parseFloat(ethers.formatUnits(tx.fee || 0, 18)),
      timestamp: Number(tx.timestamp) || Math.floor(Date.now() / 1000),
      txHash: tx.txHash === '0x0000000000000000000000000000000000000000000000000000000000000000'
        ? null
        : tx.txHash,
      createdAt: new Date((Number(tx.timestamp) || Math.floor(Date.now() / 1000)) * 1000).toISOString(),
      status: 'completed',
      toAddress: tx.counterparty || walletAddress,
      fromAddress: walletAddress,
      metadata: {
        paymentType: getTransactionTypeString(Number(tx.txType) || 2),
        isContractTx: true
      }
    }));

    setLocalContractTransactions(formattedTxs);
  };

  // Helper function to get token symbol from address
  const getTokenSymbol = (tokenAddress) => {
    if (!tokenAddress || tokenAddress === ethers.ZeroAddress) return 'ETH';

    const addressToSymbol = {
      '0x0000000000000000000000000000000000000000': 'ETH',
    };

    return addressToSymbol[tokenAddress?.toLowerCase()] || 'TOKEN';
  };

  // Helper function to get token address from symbol (reverse lookup)
  const getTokenAddressFromSymbol = (symbol) => {
    const symbolToAddress = {
      'ETH': ethers.ZeroAddress,
      // Add other tokens as needed
    };
    return symbolToAddress[symbol] || ethers.ZeroAddress;
  };

  // Helper function to determine transaction type from description
  const getTransactionTypeFromDescription = (description) => {
    if (!description) return 2; // Default to transfer

    const desc = description.toLowerCase();
    if (desc.includes('deposit')) return 0;
    if (desc.includes('withdrawal')) return 1;
    if (desc.includes('qr')) return 3;
    return 2; // transfer
  };

  // Helper function to convert transaction type enum to string
  const getTransactionTypeString = (txType) => {
    const types = {
      0: 'deposit',
      1: 'withdraw',
      2: 'transfer',
      3: 'qr_payment',
      4: 'fee_collection'
    };
    return types[txType] || 'unknown';
  };

  // Handle refresh
  const handleRefresh = async () => {
    if (refreshTransactions) {
      await refreshTransactions();
    }
    await loadContractTransactions();
  };

  // Load transactions when component mounts or dependencies change
  useEffect(() => {
    loadContractTransactions();
  }, [web3payContract, walletAddress, limit]);

  // Use provided contract transactions if available, otherwise use local state
  const transactions = contractTransactions && contractTransactions.length > 0
    ? contractTransactions
    : localContractTransactions;

  const isLoading = contractLoading;

  const formatAmount = (amount, tokenSymbol) => {
    if (!amount || !tokenSymbol) return 'N/A';
    const prefix = amount > 0 ? '+' : '';
    return `${prefix}${parseFloat(amount).toFixed(6)} ${tokenSymbol}`;
  };

  const formatAddress = (address) => {
    if (!address) return 'N/A';
    if (address.length > 20) {
      return `${address.substring(0, 6)}...${address.substring(address.length - 4)}`;
    }
    return address;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'completed':
      case 'confirmed':
        return '#00FF00';
      case 'pending':
        return '#FFAA00';
      case 'failed':
      case 'cancelled':
        return '#FF0000';
      default:
        return '#ccc';
    }
  };

  const getAmountColor = (amount) => {
    if (amount > 0) return '#00FF00';
    if (amount < 0) return '#FF0000';
    return '#FFD700';
  };

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <div>
          <h2 style={{ color: '#FFD700', marginBottom: '5px' }}>
            Transaction History (Smart Contract)
          </h2>
          <p style={{ color: '#ccc' }}>View your past transactions directly from the blockchain.</p>
        </div>
        <button
          onClick={handleRefresh}
          style={{
            background: 'transparent',
            border: '1px solid #FFD700',
            color: '#FFD700',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px',
          }}
        >
          {isLoading ? 'Loading...' : 'Refresh'}
        </button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: '15px', marginBottom: '20px' }}>
        <div>
          <label style={{ display: 'block', color: '#FFD700', marginBottom: '5px', fontSize: '14px' }}>
            Limit
          </label>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            style={{
              padding: '8px',
              border: '1px solid #FFD700',
              borderRadius: '4px',
              background: '#111',
              color: '#FFD700',
              fontSize: '14px',
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div style={{ textAlign: 'center', color: '#FFD700', padding: '20px' }}>
          <p>Loading transactions from blockchain...</p>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{
          background: '#FF0000',
          color: 'white',
          padding: '10px',
          borderRadius: '4px',
          marginBottom: '20px'
        }}>
          Error loading transactions: {error}
        </div>
      )}

      {/* Empty State */}
      {!isLoading && !error && transactions.length === 0 && (
        <div style={{
          textAlign: 'center',
          color: '#666',
          padding: '40px 20px',
          border: '1px solid #333',
          borderRadius: '8px'
        }}>
          <p>No transactions found</p>
          <p style={{ fontSize: '14px', marginTop: '10px' }}>
            Make some payments to see transaction history here.
          </p>
        </div>
      )}

      {/* Transactions Table */}
      {!isLoading && !error && transactions.length > 0 && (
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          marginTop: '15px',
          background: '#111',
          border: '1px solid #FFD700',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <thead>
            <tr>
              <th style={{
                background: '#222',
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left',
                fontWeight: 'bold',
              }}>Date</th>
              <th style={{
                background: '#222',
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left',
                fontWeight: 'bold',
              }}>Description</th>
              <th style={{
                background: '#222',
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left',
                fontWeight: 'bold',
              }}>Amount</th>
              <th style={{
                background: '#222',
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left',
                fontWeight: 'bold',
              }}>Status</th>
              <th style={{
                background: '#222',
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left',
                fontWeight: 'bold',
              }}>From/To</th>
              <th style={{
                background: '#222',
                color: '#FFD700',
                padding: '12px',
                borderBottom: '1px solid #FFD700',
                textAlign: 'left',
                fontWeight: 'bold',
              }}>TX Hash</th>
            </tr>
          </thead>
          <tbody>
            {transactions.map((tx, index) => (
              <tr key={tx.id || index}>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: '#ccc',
                }}>
                  {new Date(tx.createdAt || tx.timestamp * 1000).toLocaleDateString()}
                </td>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: '#ccc',
                }}>
                  {tx.description || tx.metadata?.paymentType || 'Transaction'}
                </td>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: getAmountColor(tx.amount)
                }}>
                  {formatAmount(tx.amount, tx.tokenSymbol)}
                </td>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: getStatusColor(tx.status)
                }}>
                  {tx.status || 'completed'}
                </td>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: '#ccc',
                }}>
                  {tx.toAddress ? formatAddress(tx.toAddress) :
                   tx.fromAddress ? formatAddress(tx.fromAddress) : 'N/A'}
                </td>
                <td style={{
                  padding: '10px 12px',
                  borderBottom: '1px solid #333',
                  color: '#ccc',
                }}>
                  {tx.txHash ? (
                    <a
                      href={`https://sepolia.etherscan.io/tx/${tx.txHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: '#FFD700', textDecoration: 'none' }}
                    >
                      {formatAddress(tx.txHash)}
                    </a>
                  ) : (
                    'Pending'
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {/* Transaction Count Info */}
      {!isLoading && transactions.length > 0 && (
        <div style={{
          marginTop: '15px',
          color: '#666',
          fontSize: '14px',
          textAlign: 'center'
        }}>
          Showing {transactions.length} transaction{transactions.length !== 1 ? 's' : ''} from smart contract
        </div>
      )}
    </div>
  );
};

export default TransactionHistory;
